'use client';

import type { ReactNode } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { BodyDefault, MetaDefault } from '../../../../../components/typography';
import { MarkdownPreview } from '../../../../markdown-preview';
import { useTranslations } from 'next-intl';
import type { OutputRenderContext } from '../../tool-config-types';
import type {
  AttackerToolOutput,
  AttackerAwaitingOutput,
  AttackerEvidence,
} from './types';
import { FlatToolCard } from '../shared/flat-tool-card';
import { AttackerExecutionTrackingRow } from './AttackerExecutionTrackingRow';
import { ViewExecutionAction } from './ViewExecutionAction';

function isAwaitingOutput(content: unknown): content is AttackerAwaitingOutput {
  return (
    typeof content === 'object' &&
    content !== null &&
    (content as AttackerAwaitingOutput).awaitingCompletion === true &&
    typeof (content as AttackerAwaitingOutput).neoThreadId === 'string'
  );
}

function isTrackingOnlyOutput(output: AttackerToolOutput): boolean {
  const record = output as AttackerToolOutput & {
    neoThreadId?: string;
  };
  const executionId = output.threadId ?? record.neoThreadId;

  return (
    output.success === undefined &&
    !output.label &&
    !output.analysis &&
    !output.evidences?.length &&
    !output.error &&
    typeof executionId === 'string' &&
    executionId.length > 0
  );
}

function parseOutput(content: unknown): AttackerToolOutput | null {
  if (!content) return null;
  if (typeof content === 'string') {
    try {
      return JSON.parse(content) as AttackerToolOutput;
    } catch {
      return null;
    }
  }

  if (typeof content === 'object') {
    return content as AttackerToolOutput;
  }

  return null;
}

function AttackerResultBody({ output }: { output: AttackerToolOutput }) {
  const evidences = output.evidences ?? [];

  return (
    <VStack alignItems="stretch" gap={2} width="100%">
      {output.analysis ? (
        <MarkdownPreview source={output.analysis} compact fontSize={14} />
      ) : null}
      {evidences.map((evidence: AttackerEvidence, index: number) => (
        <MetaDefault key={`${evidence.type}-${index}`} color="fg.muted">
          {evidence.explanation || evidence.content}
        </MetaDefault>
      ))}
      {output.error ? (
        <MetaDefault color="fg.error">{output.error}</MetaDefault>
      ) : null}
      {!output.analysis &&
      evidences.length === 0 &&
      !output.error &&
      output.message ? (
        <BodyDefault color="fg.muted">{output.message}</BodyDefault>
      ) : null}
      {output.label ? (
        <MetaDefault color="fg.muted">{output.label}</MetaDefault>
      ) : null}
    </VStack>
  );
}

export function AttackerResultView({
  content,
  signalId,
  onOpenObject,
  headerActions,
}: {
  content: unknown;
  signalId?: string;
  onOpenObject?: OutputRenderContext['onOpenObject'];
  headerActions?: ReactNode;
}) {
  const t = useTranslations('designSystem.chat.attackerTool');

  if (isAwaitingOutput(content)) {
    return (
      <AttackerExecutionTrackingRow
        executionId={content.neoThreadId}
        signalId={content.signalId ?? signalId}
        signalTitle={content.signalTitle}
        isRunning={false}
        onOpenObject={onOpenObject}
        headerActions={headerActions}
      />
    );
  }

  const output = parseOutput(content);

  if (output && isTrackingOnlyOutput(output)) {
    const executionId =
      output.threadId ??
      (
        output as AttackerToolOutput & {
          neoThreadId?: string;
        }
      ).neoThreadId;

    if (executionId) {
      return (
        <AttackerExecutionTrackingRow
          executionId={executionId}
          signalId={output.signalId ?? signalId}
          signalTitle={output.signalTitle}
          isRunning={false}
          onOpenObject={onOpenObject}
          headerActions={headerActions}
        />
      );
    }
  }

  if (!output) {
    const fallbackText =
      typeof content === 'string'
        ? content
        : content != null
          ? JSON.stringify(content, null, 2)
          : undefined;

    return (
      <FlatToolCard title={t('attackFailed')} headerActions={headerActions}>
        {fallbackText ? (
          <MetaDefault color="fg.muted">{fallbackText}</MetaDefault>
        ) : undefined}
      </FlatToolCard>
    );
  }

  const isSuccess = output.success === true;
  const executionId = output.threadId ?? undefined;
  const hasBody = Boolean(
    output.analysis ||
      output.evidences?.length ||
      output.error ||
      output.message ||
      output.label,
  );

  return (
    <FlatToolCard
      title={isSuccess ? t('attackCompleted') : t('attackFailed')}
      headerActions={
        <>
          {headerActions}
          {executionId ? (
            <ViewExecutionAction
              executionId={executionId}
              onOpenObject={onOpenObject}
            />
          ) : null}
        </>
      }
    >
      {hasBody ? <AttackerResultBody output={output} /> : undefined}
    </FlatToolCard>
  );
}

export function renderAttackerResult(ctx: OutputRenderContext): ReactNode {
  return (
    <AttackerResultView content={ctx.content} onOpenObject={ctx.onOpenObject} />
  );
}
